import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Pagination } from '@mui/material';
import Dropdown from '../../../../components/common/dropdowns/Dropdown';
import useGameCheckedAnswers from '../../../../hooks/api/games/useGameCheckedAnswers';
import useGameReviewAnswers from '../../../../hooks/api/games/useGameReviewAnswers';
import useGameUsers from '../../../../hooks/api/games/useGameUsers';
import { IGameAnswer, IQuestion } from '../../../../types/ApiTypes';
import { DropdownOption } from '../../../../types/Dropdown.types';
import { TypeFromConstObject } from '../../../../types/Types';
import SingleActiveGamePageWrapper from '../../SingleActiveGamePageWrapper';
import CheckedAnswers from './components/checkedAnswers/CheckedAnswers';
import ReviewAnswers from './components/reviewAnswers/ReviewAnswers';
import useSetInitialAnswerTeam from './hooks/useSetInitialAnswerTeam';
import useTeamsDropdownOptions from './hooks/useTeamsDropdownOptions';
import { groupAnswersByQuestion } from './utils/groupAnswersByQuestion';

export const AnswersOptions = {
  UNCHECKED: 'unchecked',
  CHECKED: 'checked',
} as const;

export type AnswersOption = TypeFromConstObject<typeof AnswersOptions>;

export const ALL_TEAMS_ID = 'all';

// GMT-795: liczba GRUP PYTAŃ na stronę (widok grupuje odpowiedzi po pytaniu)
const QUESTION_GROUPS_PER_PAGE = 5;

export interface IAnswersGroup {
  question: IQuestion;
  answers: IGameAnswer[];
}

const SingleActiveGameAnswersView = () => {
  const { t } = useTranslation();
  const params = useParams();
  const gameId = params.id as string;

  const answersOptions: DropdownOption[] = [
    {
      id: AnswersOptions.UNCHECKED,
      value: t('singleActiveGame.answers.answersToReview'),
    },
    {
      id: AnswersOptions.CHECKED,
      value: t('singleActiveGame.answers.answersChecked'),
    },
  ];

  const [selectedAnswersOption, setSelectedAnswersOption] = useState(answersOptions[0]);
  const [reviewPage, setReviewPage] = useState(1);
  const [checkedPage, setCheckedPage] = useState(1);

  const showAnswersToReview = selectedAnswersOption?.id === AnswersOptions.UNCHECKED;
  const showAnswersChecked = selectedAnswersOption?.id === AnswersOptions.CHECKED;

  const { gameUsers } = useGameUsers({ gameId });

  const { allTeamsDropdownOption, gameUsersDropdownOptions } = useTeamsDropdownOptions({ gameUsers });
  const [selectedGameUser, setSelectedGameUser] = useState<DropdownOption | undefined>(allTeamsDropdownOption);

  useSetInitialAnswerTeam({ teamsDropdownOptions: gameUsersDropdownOptions, setSelectedTeam: setSelectedGameUser });

  // GMT-795: filtr drużyny idzie do backendu (klientowy obejmowałby tylko
  // bieżącą stronę), a paginacja jest server-side po grupach pytań
  const userGameId = selectedGameUser?.id !== ALL_TEAMS_ID ? selectedGameUser?.id : undefined;

  const handleTeamChange = (option: DropdownOption) => {
    setSelectedGameUser(option);
    setReviewPage(1);
    setCheckedPage(1);
  };

  const {
    gameReviewAnswers,
    groupsCount: reviewGroupsCount,
    isLoading: isLoadingReviewAnswers,
  } = useGameReviewAnswers({
    gameId,
    page: reviewPage,
    limit: QUESTION_GROUPS_PER_PAGE,
    userGameId,
  });
  const {
    gameCheckedAnswers,
    groupsCount: checkedGroupsCount,
    isLoading: isLoadingCheckedAnswers,
  } = useGameCheckedAnswers({
    gameId,
    enabled: showAnswersChecked,
    page: checkedPage,
    limit: QUESTION_GROUPS_PER_PAGE,
    userGameId,
  });

  const reviewAnswersToShow = useMemo(() => groupAnswersByQuestion(gameReviewAnswers), [gameReviewAnswers]);
  const checkedAnswersToShow = useMemo(() => groupAnswersByQuestion(gameCheckedAnswers), [gameCheckedAnswers]);

  const reviewPagesCount = Math.max(1, Math.ceil((reviewGroupsCount ?? 0) / QUESTION_GROUPS_PER_PAGE));
  const checkedPagesCount = Math.max(1, Math.ceil((checkedGroupsCount ?? 0) / QUESTION_GROUPS_PER_PAGE));

  return (
    <SingleActiveGamePageWrapper>
      <div className="flex justify-between mt-8 pr-8">
        <Dropdown
          options={answersOptions}
          currentOption={selectedAnswersOption}
          setNewOption={setSelectedAnswersOption}
          wrapperClassName="w-fit"
        />
        <Dropdown
          options={gameUsersDropdownOptions}
          currentOption={selectedGameUser}
          setNewOption={handleTeamChange}
          wrapperClassName="w-fit"
        />
      </div>

      <div className="py-8">
        {showAnswersToReview && (
          <>
            <ReviewAnswers answers={reviewAnswersToShow} isLoading={isLoadingReviewAnswers} />
            {reviewPagesCount > 1 && (
              <div className="flex justify-center mt-4">
                <Pagination count={reviewPagesCount} page={reviewPage} onChange={(_, page) => setReviewPage(page)} />
              </div>
            )}
          </>
        )}
        {showAnswersChecked && (
          <>
            <CheckedAnswers answers={checkedAnswersToShow} isLoading={isLoadingCheckedAnswers} />
            {checkedPagesCount > 1 && (
              <div className="flex justify-center mt-4">
                <Pagination count={checkedPagesCount} page={checkedPage} onChange={(_, page) => setCheckedPage(page)} />
              </div>
            )}
          </>
        )}
      </div>
    </SingleActiveGamePageWrapper>
  );
};

export default SingleActiveGameAnswersView;
